import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/auth';
import { supabase } from '@/integrations/supabase/client';
import { refreshTokenForRoleChanges } from '@/utils/jwt-roles';
import { toast } from '@/components/ui/use-toast';
import { Search, Edit, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useSuperAdminUsers } from '@/hooks/useSuperAdminUsers';

// Import role utilities
import { 
  getRoleDisplayName, 
  getRoleBadgeVariant,
  getRoleDescription,
  hasRoleAccess
} from '@/utils/role-utils';

interface SuperAdminRolesTabProps {
  organisations: any[];
}

const SuperAdminRolesTab: React.FC<SuperAdminRolesTabProps> = ({ organisations }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role: ''
  });
  const { user, refreshSession } = useAuth();
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Get users with our custom hook
  const { 
    users, 
    totalUsers, 
    isLoading, 
    error,
    refetch,
    fetchUserDetail,
    updateUserRole
  } = useSuperAdminUsers({
    page,
    limit: pageSize,
    search: searchQuery,
    role: roleFilter !== 'all' ? roleFilter : undefined,
    organisation_id: orgFilter !== 'all' ? orgFilter : undefined
  });
  
  // Total pages calculation
  const totalPages = Math.ceil(totalUsers / pageSize);
  
  // Update edit form when selected user changes
  useEffect(() => {
    if (selectedUser) {
      setEditForm({
        name: selectedUser.name || '',
        email: selectedUser.email || '',
        role: selectedUser.role || ''
      });
    }
  }, [selectedUser]);
  
  const handleEditRole = (user: any) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || ''
    });
    setEditDialogOpen(true);
  };
  
  const handleSaveRoleChanges = async () => {
    if (!selectedUser) return;
    
    if (editForm.role === 'super_admin' && selectedUser.role !== 'super_admin') {
      setShowConfirmation(true);
      return;
    }
    
    await updateUserRole(selectedUser.id, editForm.role);
  };

  const handleConfirmSuperAdmin = async () => {
    if (!selectedUser) return;
    await updateUserRole(selectedUser.id, 'super_admin');
    setShowConfirmation(false);
    setEditDialogOpen(false);
  };
  
  // Get organisation name for a user
  const getOrganisationName = (orgId: string) => {
    const org = organisations.find(o => o.id === orgId);
    return org ? org.name : 'None';
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold">Role Management</h2>
        
        <div className="flex w-full sm:w-auto gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground/70" />
            <Input
              placeholder="Search users..."
              className="pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            defaultValue="all"
            value={roleFilter}
            onValueChange={(value) => setRoleFilter(value)}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="super_admin">Super Admin</SelectItem>
              <SelectItem value="org_admin">Org Admin</SelectItem>
              <SelectItem value="rest_admin">Rest Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value="new_sign_up">New Sign Up</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
            </SelectContent>
          </Select>
          
          <Select
            defaultValue="all"
            value={orgFilter}
            onValueChange={(value) => setOrgFilter(value)}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Organisations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organisations</SelectItem>
              <SelectItem value="none">No Organisation</SelectItem>
              {organisations.map(org => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="p-4 bg-muted rounded-md mb-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
          <div>
            <h3 className="font-medium mb-1">Role Management Guidelines</h3>
            <p className="text-sm text-muted-foreground">
              Changing user roles affects their access level in the system. Super Admin should only be assigned to company employees.
              Ensure users have the appropriate permissions needed for their role. 
              Role changes take effect immediately for new sessions.
            </p>
          </div>
        </div>
      </div>
      
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Current Role</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array(5).fill(0).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                    <TableCell className="text-right"><Skeleton className="h-6 w-8 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4 text-destructive">
                    Error loading users: {error}
                  </TableCell>
                </TableRow>
              ) : users && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    No users found
                  </TableCell>
                </TableRow>
              ) : users && users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id} className="group">
                    <TableCell>
                      <button
                        onClick={() => handleEditRole(user)}
                        className="font-medium cursor-pointer transition-colors hover:text-primary active:text-primary"
                      >
                        {user.name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleEditRole(user)}
                        className="cursor-pointer transition-colors hover:text-primary active:text-primary"
                      >
                        {user.email}
                      </button>
                    </TableCell>
                    <TableCell>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant={getRoleBadgeVariant(user.role)}>
                              {getRoleDisplayName(user.role)}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{getRoleDescription(user.role)}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                    <TableCell>
                      {user.organisation_id ? user.organisation_name || getOrganisationName(user.organisation_id) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleEditRole(user)}
                        className="opacity-0 group-hover:opacity-100"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only">Edit role</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          
          {/* Pagination controls */}
          {!isLoading && !error && (
            <div className="flex items-center justify-between px-4 py-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {users && users.length > 0 ? (page - 1) * pageSize + 1 : 0} - {Math.min(page * pageSize, totalUsers)} of {totalUsers} users
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm">
                  Page {page} of {totalPages || 1}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Role</DialogTitle>
            <DialogDescription>
              Change the role for this user. Role changes affect permissions and access levels.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editForm.name}
                readOnly
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={editForm.email}
                readOnly
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={editForm.role}
                onValueChange={(value) => setEditForm({...editForm, role: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="rest_admin">Restaurant Admin</SelectItem>
                  <SelectItem value="org_admin">Organisation Admin</SelectItem>
                  <SelectItem value="new_sign_up">New Sign Up</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  {user?.role === 'super_admin' && (
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <div className="text-sm text-muted-foreground mt-1 p-2 border rounded-md">
                {getRoleDescription(editForm.role)}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleSaveRoleChanges}
              disabled={editForm.role === selectedUser?.role}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Confirm Super Admin Role</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to grant <strong>{selectedUser?.name}</strong> Super Admin privileges.
              This will give them full access to all system data and settings.
              <p className="font-semibold mt-4">
                Super Admin should only be assigned to company employees.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmation(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmSuperAdmin}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirm Super Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SuperAdminRolesTab;